import { useEffect, useMemo, useRef, useState } from "react";
import GithubImportModal from "@/components/Github/GithubImportModal";
import { parseGithubRepo } from "@/utils/githubRepoUrl";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2, Check, ChevronLeft, ChevronRight, Code2, ArrowLeftRight, ShieldCheck, Lock,
  Wallet, CreditCard, Sparkles,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/utils/appsettings";
import { useAuthToken } from "@/contexts/AuthContext";
import { ENGAGE_CATEGORIES } from "./engageCategories";
import { SERVICE_COPY, PRODUCTION_TIERS, CO_BUILD_UPSELL, type EngageServiceKey } from "./engageServiceCopy";
import EngageServiceCards from "./EngageServiceCards";
import { computeRedemptionOptions } from "@/utils/redemptionOptions";
import { quoteEngagement } from "@/utils/engagePricing";
import {
  ENGAGEMENT_MIN_HOURS, HOUR_PACKAGES, SERVICE_COSTS,
  formatHourPrice, formatUSD,
} from "@/utils/expertHours";
import { useEngagement } from "@/hooks/useEngagement";
import { useEngageServices, type EngageService } from "@/hooks/useEngageServices";
import {
  useHoursPayment, HoursPaymentFields, HoursAddressFields, HoursPaymentValidationError,
} from "@/components/billing/InlineHoursPayment";
import { PromoCodeInput } from "@/components/PromoCodeInput";
import { TaxAndBusinessFields } from "@/components/billing/TaxAndBusinessFields";
import { WalletPayButton } from "@/components/stripe/WalletPayButton";

interface EngageChooserProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  onEngaged?: (projectId?: string) => void;
  /** Standalone migration with no project yet: called at submit with the "where it lives" repo link, before any charge, and returns the new project's id. */
  createProject?: (origin: string) => Promise<string>;
  /** Editable prefill for migration's "Where does it live now" (initialOrigin is the read-only variant). */
  defaultOrigin?: string;
  /** Rendered under the origin field, e.g. a "Connect GitHub" prompt; false keeps Next disabled. */
  originNotice?: React.ReactNode;
  originReady?: boolean;
  /** Pre-fill the category step (still editable) — e.g. when arriving from a specific
   * in-app prompt that already knows what kind of help is needed. */
  initialCategory?: string;
  /** Pre-fill the description textarea (still editable). */
  initialDescription?: string;
  /** Pre-fill migration's "Where does it live now" field. When set, this is shown as a
   * known fact instead of an editable input — e.g. an import already knows the repo, so
   * re-asking is redundant. */
  initialOrigin?: string;
  /** Ranks migration first, since that is the likely intent for an imported project. */
  isGithubImport?: boolean;
  /** Mobile apps are not published by the platform, so production cover does not apply. */
  isMobile?: boolean;
  /** Opens straight into this service, skipping the service step (menu deep-links). */
  initialService?: EngageServiceKey;
  /** When set, opens straight into a migration sized as a whole rather than the service
   * chooser, and pins its hours. Used by the in-chat and editor migration prompts. */
  fixedHours?: number;
  /** Marketing price shown alongside fixedHours, e.g. "$2,000 fixed price". */
  fixedPriceLabel?: string;
  /** For a fixedHours package only: never let an existing hours balance discount it — always
   * charge the full SKU price. Imports are billed as a flat per-project fee, not drawn from
   * a general balance the user might happen to be sitting on. */
  requirePayment?: boolean;
  /** Renders as plain content inside the caller's own panel instead of a Radix Dialog. */
  embedded?: boolean;
}

type Step = "service" | "category" | "hours" | "details" | "tier" | "confirm";

const SERVICE_ICONS: Record<EngageServiceKey, typeof Code2> = {
  development: Code2,
  migration: ArrowLeftRight,
  production: ShieldCheck,
};

/**
 * Guided expert engagement: pick a service → configure it → confirm. Hours already in the
 * balance are redeemed outright; a shortfall is paid for inline on the same screen, so the
 * user is never bounced out to a separate purchase modal mid-flow.
 */
export default function EngageChooser({
  open, onOpenChange, projectId, onEngaged, createProject, defaultOrigin, originNotice, originReady = true, initialCategory, initialDescription, initialOrigin,
  isGithubImport, isMobile, fixedHours, fixedPriceLabel, requirePayment = false, initialService,
  embedded = false,
}: EngageChooserProps) {
  const { toast } = useToast();
  const token = useAuthToken();
  const [switchingToAi, setSwitchingToAi] = useState(false);
  const {
    hours: balance, totalAvailableHours, requestEngagement, requesting,
    hasActiveEngagement, activeEngagementStatus,
  } = useEngagement(projectId);
  const { services, isCoBuild } = useEngageServices({
    projectId, balanceHours: totalAvailableHours, isGithubImport, isMobile,
  });

  // First step of a service's own configuration, used both for deep links and for Back.
  const firstStepOf = (key: EngageServiceKey): Step =>
    key === "development" ? "category" : key === "migration" ? "details" : "tier";

  // A caller-pinned package or a menu deep link means the service is already decided, so the
  // chooser opens straight into that flow rather than making the user pick it again.
  const presetService: EngageServiceKey | null =
    initialService ?? (fixedHours != null ? "migration" : null);
  const [step, setStep] = useState<Step>(presetService ? firstStepOf(presetService) : "service");
  // Whichever element actually scrolls never reset on step change, so a scroll position left
  // over from a taller previous step carried into the next one, clipping/overlapping its title
  // and top content. Which element that is differs by render path: in the Dialog case topRef IS
  // the scrolling element (overflow-y-auto is on DialogContent itself) — reset its own scrollTop;
  // embedded, the scroll instead lives on ProjectSettingsShell's container outside this
  // component entirely — scrollIntoView walks up to find and reset that ancestor. Doing both is
  // harmless in either case since the inapplicable one is a no-op.
  const topRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!topRef.current) return;
    topRef.current.scrollTop = 0;
    topRef.current.scrollIntoView({ block: "start" });
  }, [step]);
  const [service, setService] = useState<EngageServiceKey | null>(presetService);
  const [category, setCategory] = useState<string | null>(initialCategory ?? null);
  const [description, setDescription] = useState(initialDescription ?? "");
  const [devHours, setDevHours] = useState<number | null>(null);
  const [tier, setTier] = useState<"production_essential" | "production_advanced">("production_advanced");
  const [origin, setOrigin] = useState(initialOrigin ?? defaultOrigin ?? "");
  const [quirks, setQuirks] = useState("");
  // Hours credited by a payment that succeeded before the request itself did, so a retry
  // redeems what was already bought instead of charging the card twice.
  const [creditedHours, setCreditedHours] = useState(0);
  // Kept so a retry after a failed payment reuses the project instead of importing the repo twice
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);
  const originRepo = parseGithubRepo(origin);

  // Migration picked with no caller-pinned fixedHours means this is a fresh "bring a project
  // onto Joylo" pick from the general service list (Home/Help/Billing/project-page menus) —
  // NOT the $2,000 fixed-price Architect step of an import already in progress (that always
  // arrives with fixedHours set, and never goes through this redirect). A fresh pick hands
  // off entirely to the same repo-selection flow "New Project → Import from GitHub" uses,
  // instead of the old free-text "where does it live" form that could redeem hours balance —
  // migration is never balance-eligible, and starting the real import is strictly better.
  const isFreshMigrationPick = service === "migration" && fixedHours == null;
  const [showGithubImport, setShowGithubImport] = useState(false);

  const effectiveBalance = totalAvailableHours + creditedHours;
  const redemptionOptions = useMemo(
    () => computeRedemptionOptions(effectiveBalance),
    [effectiveBalance],
  );

  // What the chosen service will consume. Development is user-sized; the rest are fixed.
  // A caller-supplied fixedHours pins the migration size only, so the other services keep
  // their own costs if the user navigates to them.
  const requiredHours = useMemo(() => {
    if (service === "migration") return fixedHours ?? SERVICE_COSTS.migration.hours;
    if (service === "production") return SERVICE_COSTS[tier].hours;
    return devHours ?? redemptionOptions[0] ?? ENGAGEMENT_MIN_HOURS;
  }, [service, tier, devHours, redemptionOptions, fixedHours]);

  const fixedCost = service === "migration"
    ? SERVICE_COSTS.migration
    : service === "production" ? SERVICE_COSTS[tier] : null;

  const quote = useMemo(() => quoteEngagement({
    requiredHours,
    balanceHours: effectiveBalance,
    sku: fixedCost?.sku,
    skuUSD: fixedCost?.displayUSD,
    ignoreBalance: requirePayment,
  }), [requiredHours, effectiveBalance, fixedCost, requirePayment]);

  const payment = useHoursPayment(quote.paymentTarget);

  // Co-Build already bundles production cover, so there is nothing to buy or redeem.
  const includedInPlan = service === "production" && isCoBuild;
  const copy = service ? SERVICE_COPY[service] : null;
  const availableWhole = Math.max(0, Math.floor(effectiveBalance));
  // Balance is always spent first; only what it cannot cover is ever charged — except a
  // requirePayment package, which never draws on it at all, by design.
  const redeemFromBalance = requirePayment ? 0 : Math.min(availableWhole, requiredHours);
  const remainingAfter = Math.max(0, availableWhole + quote.shortfallHours - requiredHours);

  const reset = () => {
    setStep(presetService ? firstStepOf(presetService) : "service");
    setService(presetService);
    setCategory(initialCategory ?? null);
    setDescription(initialDescription ?? "");
    setDevHours(null);
    setTier("production_advanced");
    setOrigin(initialOrigin ?? defaultOrigin ?? "");
    setQuirks("");
    setCreditedHours(0);
    payment.resetCard();
  };

  const close = (v: boolean) => {
    if (!v) reset();
    onOpenChange(v);
  };

  useEffect(() => {
    // Deliberately does NOT call close()/onOpenChange here — that would tell the parent
    // (Home/Help/Billing/project-page menus) to close, unmounting this EngageChooser instance
    // and the GithubImportModal it's about to render right along with it. The Dialog above is
    // already hidden via its own `open` condition; this component just stays mounted quietly
    // underneath so GithubImportModal has somewhere to live until the import actually starts.
    if (open && isFreshMigrationPick) {
      setShowGithubImport(true);
    }
  }, [open, isFreshMigrationPick]);

  // Backs out of the pending paid Architect engagement and resumes the normal AI-driven
  // import instead — only reachable for an import awaiting that payment (isGithubImport).
  const switchToAiImport = async () => {
    setSwitchingToAi(true);
    try {
      await axiosInstance.post(
        `/agent/workflows/${projectId}/signal/switch-to-ai-import`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({
        title: "Resuming AI import",
        description: "Your AI agents are picking this import back up.",
      });
      onEngaged?.();
      close(false);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Couldn't switch to AI import",
        description: error.response?.data?.detail || error.message || "Please try again.",
      });
    } finally {
      setSwitchingToAi(false);
    }
  };

  const pickService = (key: EngageServiceKey) => {
    setService(key);
    setStep(firstStepOf(key));
  };

  const back = () => {
    // A standalone migration has no project for the service list to act on, so backing out of it closes
    if (createProject && !projectId && step === "details") {
      close(false);
      return;
    }
    if (step === "confirm") {
      setStep(service === "development" ? "hours" : firstStepOf(service ?? "development"));
    } else if (step === "hours") {
      setStep("category");
    } else {
      // Back out of a service to the full list, so a deep link is never a dead end.
      setService(null);
      setStep("service");
    }
  };

  // Migration and production have no category of their own, so the intent is carried in the
  // description where whoever picks the request up will read it.
  const composeDescription = () => {
    const notes = description.trim();
    if (service === "migration") {
      const detail = [origin.trim() && `Currently on: ${origin.trim()}`, quirks.trim()]
        .filter(Boolean).join("\n");
      return [`${copy?.descriptionPrefix} — ${SERVICE_COSTS.migration.hours}h committed`, detail, notes]
        .filter(Boolean).join("\n");
    }
    if (service === "production") {
      const name = SERVICE_COSTS[tier].label;
      return [`${copy?.descriptionPrefix} (${name}) — one month of cover`, notes]
        .filter(Boolean).join("\n");
    }
    return notes;
  };

  const submit = async (walletPaymentMethodId?: string) => {
    // Tracked locally because the catch block runs before a setState has been applied.
    let paidNow = 0;
    let phase: "project" | "payment" | "request" = "payment";
    let targetProjectId = projectId || createdProjectId || "";
    try {
      if (!targetProjectId && createProject) {
        phase = "project";
        targetProjectId = await createProject(origin.trim());
        setCreatedProjectId(targetProjectId);
        phase = "payment";
      }
      if (quote.needsPayment && quote.paymentTarget) {
        const result = await payment.pay(quote.paymentTarget, walletPaymentMethodId);
        paidNow = result.hours_purchased || quote.shortfallHours;
        // Counted locally rather than refetching the balance, so the purchased hours are
        // never added twice, and a retry redeems them instead of charging the card again.
        setCreditedHours((h) => h + paidNow);
      }

      phase = "request";
      await requestEngagement({
        hours: requiredHours,
        category: (service === "development" ? category : copy?.category) ?? undefined,
        description: composeDescription() || undefined,
        // Fixed-scope services carry their SKU so the server can seed the board checklist.
        serviceType: fixedCost?.sku,
        projectId: targetProjectId,
      });

      onEngaged?.(targetProjectId);
      close(false);
    } catch (error) {
      if (phase === "project") {
        toast({
          variant: "destructive",
          title: "Couldn't set up your project",
          description: error instanceof Error ? error.message : "Check the repository link and try again. Nothing was charged.",
        });
      } else if (error instanceof HoursPaymentValidationError) {
        toast({ variant: "destructive", title: "Check your card details", description: error.message });
      } else if (phase === "payment") {
        toast({
          variant: "destructive",
          title: "Payment failed",
          description: error instanceof Error ? error.message : "Payment processing failed",
        });
      } else if (paidNow > 0 || creditedHours > 0) {
        // Paid, but the request did not land. useEngagement already toasted the cause, so
        // this only explains that the hours are safe and a retry will not re-charge.
        toast({
          variant: "destructive",
          title: "Hours added, request not sent",
          description: "Your hours are safe in your balance. Tap Retry to send the request — you will not be charged again.",
        });
      }
      // A plain request failure with nothing paid is already surfaced by useEngagement.
    }
  };

  const busy = requesting || payment.processing;
  const categoryLabel = ENGAGE_CATEGORIES.find((c) => c.key === category)?.label ?? "";

  // Address-then-card gating only matters once a card is actually needed — a balance-covered
  // engagement never touches payment.addressStep at all.
  const paymentStep = payment.addressStep;
  // The preview (tax + any applied voucher) resolves as soon as the address is valid, not gated
  // to the payment step, so it's already visible where the voucher field now lives (address step).
  const resolvingTaxPreview = quote.needsPayment && payment.addressValid && payment.resolvingPreview;
  const taxPercentage = payment.preview?.tax_breakdown?.[0]?.percentage;
  const taxLabel = `VAT${taxPercentage ? ` (${taxPercentage}%)` : ""}`;
  // The server-resolved, tax-inclusive total once the address is valid and the preview has come
  // back; the client-computed pre-tax quote otherwise.
  const displayTotalUSD = quote.needsPayment && payment.addressValid && payment.preview
    ? payment.preview.total
    : quote.chargeUSD;

  const ctaLabel = () => {
    if (includedInPlan) return "Advanced cover is already active";
    if (creditedHours > 0 && !quote.needsPayment) return `Retry — redeem ${requiredHours}h`;
    if (quote.needsPayment) return `Pay ${formatUSD(displayTotalUSD)} and send request`;
    return `Redeem ${requiredHours}h and send request`;
  };

  const hint = () => {
    if (includedInPlan) return "Included with your Co-Build plan. Nothing to pay.";
    if (quote.needsPayment) return "Secured by Stripe. Hours are added to your balance and committed to this engagement.";
    return "Paid from your architect hours. Nothing is charged to your card.";
  };

  // Title/description content is identical whether this renders inside a Radix Dialog or as
  // plain embedded content — only the wrapping element (DialogTitle/DialogDescription vs a
  // plain h2/p) differs, so both branches below share these nodes.
  const titleText = copy ? copy.heading : "Engage a Joylo expert";
  const descriptionContent = (
    <>
      {step === "service" && "What do you need help with?"}
      {step === "category" && "What do you need help with on this project?"}
      {step === "hours" && "How many hours should we allocate to this project?"}
      {step === "details" && copy?.subheading}
      {step === "tier" && "Choose a level of cover."}
      {step === "confirm" && "Review and confirm your request."}
    </>
  );

  // Everything between the header and the footer — no Dialog primitives are used here, so
  // this block renders identically whether embedded or inside the Radix Dialog.
  const stepsContent = (
    <>
      {/* Wallet strip — the balance leads, so the user always sees what they already
          hold before any price or card field is put in front of them. Skipped entirely
          for requirePayment: the balance plays no part in this charge, so surfacing it
          (and restating the hour commitment) would only read as contradicting the fixed
          price above. */}
      {!includedInPlan && !requirePayment && (
        <div className={`relative overflow-hidden rounded-2xl border px-4 py-3.5 ${
          availableWhole > 0
            ? "border-emerald-200 bg-gradient-to-r from-emerald-50 to-emerald-50/30 dark:border-emerald-900/40 dark:from-emerald-950/30 dark:to-emerald-950/10"
            : "border-line bg-soft dark:border-white/10 dark:bg-white/5"
        }`}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <span className={`w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center ${
                availableWhole > 0 ? "bg-emerald-100 dark:bg-emerald-950/40" : "bg-soft"
              }`}>
                <Wallet className={`w-4 h-4 ${availableWhole > 0 ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}`} />
              </span>
              <div>
                <div className="text-sm font-bold text-foreground leading-tight">
                  {availableWhole > 0
                    ? `${availableWhole} architect ${availableWhole === 1 ? "hour" : "hours"} available`
                    : "No architect hours yet"}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {availableWhole > 0
                    ? (balance?.monthly_allocated || 0) > 0
                      ? "Your plan allocation plus any hours you have bought"
                      : "Usable across all your projects"
                    : "Buy hours once, use them on any project"}
                </div>
              </div>
            </div>
            {step !== "service" && (
              <div className="text-right">
                {quote.needsPayment && redeemFromBalance > 0 ? (
                  <>
                    <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                      Redeeming {redeemFromBalance}h from your balance
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Only the remaining {quote.shortfallHours}h is charged
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-xs font-semibold text-foreground">
                      Commits {requiredHours}h to this project
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {quote.needsPayment
                        ? requirePayment
                          ? "Billed as a fixed project fee, not drawn from your balance"
                          : "Nothing in your balance to draw on yet"
                        : "Nothing charged to your card"}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------- STEP: SERVICE ---------- */}
      {step === "service" && (
        <EngageServiceCards services={services} onPick={pickService} />
      )}

      {/* ---------- context bar for every configured service ---------- */}
      {step !== "service" && copy && (
        <div className="rounded-xl border border-line bg-selected px-4 py-3.5 dark:border-plum/40 dark:bg-selected">
          <div className="text-sm font-semibold text-foreground">
            {includedInPlan ? "Advanced cover is already active on your account" : copy.contextTitle}
          </div>
          <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
            {includedInPlan
              ? "Co-Build includes Advanced production support. There is nothing to buy here — everything below is already running."
              : copy.contextBody}
          </div>
        </div>
      )}

      {/* ---------- STEP: CATEGORY ---------- */}
      {step === "category" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {ENGAGE_CATEGORIES.map((c) => {
              const selected = category === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setCategory(c.key)}
                  className={`text-left rounded-xl border p-3 transition-colors ${
                    selected
                      ? "border-plum/40 bg-selected ring-1 ring-line dark:border-plum/40 dark:bg-selected dark:ring-line"
                      : "border-line hover:border-plum/40 bg-white dark:border-white/10 dark:hover:border-plum/40 dark:bg-white/5"
                  }`}
                >
                  <div className="text-sm font-semibold text-foreground">{c.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{c.example}</div>
                </button>
              );
            })}
          </div>
          <Textarea
            placeholder="Tell the architect what is happening. Payments work in test mode but fail live, orders save with no payment reference, and you cannot see why."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="rounded-xl resize-none bg-white text-foreground placeholder:text-muted-foreground border-line focus:border-plum/40 focus-visible:ring-1 focus-visible:ring-ring/40 dark:bg-white/5 dark:text-white dark:placeholder:text-gray-500 dark:border-white/10 dark:focus:border-plum/40 dark:focus-visible:ring-ring/40"
          />
        </div>
      )}

      {/* ---------- STEP: HOURS (development) ---------- */}
      {step === "hours" && (
        <div className="space-y-4">
          {redemptionOptions.length > 0 && (
            <>
              <p className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground">
                Allocate from your balance
              </p>
              <div className="flex flex-wrap gap-2">
                {redemptionOptions.map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setDevHours(h)}
                    className={`px-4 py-2 rounded-xl border text-sm font-semibold transition-colors ${
                      requiredHours === h
                        ? "border-plum/40 bg-selected text-foreground ring-1 ring-line dark:border-plum/40 dark:bg-selected dark:text-white dark:ring-line"
                        : "border-line hover:border-plum/40 text-muted-foreground bg-white dark:border-white/10 dark:hover:border-plum/40 dark:text-gray-300 dark:bg-white/5"
                    }`}
                  >
                    {h}h
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                After this request you'll have{" "}
                <strong className="text-foreground">{remainingAfter}h</strong> left for other projects.
              </p>
            </>
          )}

          {/* Blocks to buy — shown when the balance cannot open an engagement on its own */}
          {availableWhole < ENGAGEMENT_MIN_HOURS && (
            <>
              <p className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground">
                Choose a block
              </p>
              {availableWhole > 0 && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 flex items-start gap-2.5 dark:border-emerald-900/40 dark:bg-emerald-950/30">
                  <Wallet className="w-3.5 h-3.5 text-emerald-700 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-emerald-800 dark:text-emerald-300 leading-relaxed">
                    Your <strong>{availableWhole}h</strong> is used first. An engagement needs{" "}
                    {ENGAGEMENT_MIN_HOURS}h to start, so you only pay for the hours beyond what you
                    already hold — prices below already have your balance taken off.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {HOUR_PACKAGES.map((pkg) => {
                  // Price shown is what this block would actually cost after the balance is spent.
                  const payable = Math.max(0, pkg.hours - availableWhole);
                  const discounted = payable < pkg.hours;
                  const selected = requiredHours === pkg.hours;
                  return (
                    <button
                      key={pkg.hours}
                      type="button"
                      onClick={() => setDevHours(pkg.hours)}
                      className={`relative text-left rounded-2xl border p-3 transition-all duration-200 ${
                        selected
                          ? "border-sunset-coral bg-sunset-coral/5  dark:border-rose-500/60 dark:bg-rose-950/20"
                          : "border-line hover:border-sunset-coral/50 hover:-translate-y-0.5 bg-white dark:border-white/10 dark:hover:border-rose-500/40 dark:bg-white/5"
                      }`}
                    >
                      {pkg.tag && (
                        <span className="absolute -top-2 left-2.5 bg-gradient-sunset text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                          {pkg.tag}
                        </span>
                      )}
                      <div className="text-lg font-bold text-foreground leading-none mt-1">{pkg.hours}h</div>
                      <div className="text-[11px] text-muted-foreground mt-1 leading-snug">{pkg.blurb}</div>
                      <div className="mt-2 flex items-baseline gap-1.5 flex-wrap">
                        <span className={`text-sm font-bold ${selected ? "text-sunset-coral dark:text-rose-400" : "text-foreground"}`}>
                          {formatHourPrice(payable)}
                        </span>
                        {discounted && (
                          <span className="text-[11px] text-muted-foreground line-through">
                            {formatHourPrice(pkg.hours)}
                          </span>
                        )}
                      </div>
                      {discounted && (
                        <div className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 mt-0.5">
                          {Math.min(availableWhole, pkg.hours)}h from balance
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Hours work across all your projects, and you redeem them in these same units.
              </p>
              <div className="rounded-xl border border-dashed border-line bg-soft p-4 dark:border-white/10 dark:bg-white/5">
                <div className="text-sm font-semibold text-foreground">{CO_BUILD_UPSELL.title}</div>
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{CO_BUILD_UPSELL.body}</div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ---------- STEP: DETAILS (migration) ---------- */}
      {step === "details" && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-sunset-coral bg-sunset-coral/5 p-4 dark:border-rose-500/50 dark:bg-rose-950/20">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <div className="text-base font-semibold text-foreground">Project migration</div>
              <div className="text-right">
                <div className="text-2xl font-bold text-sunset-coral dark:text-rose-400 leading-none">
                  {formatUSD(SERVICE_COSTS.migration.displayUSD)}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  {fixedPriceLabel ?? `${requiredHours} architect hours`}
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            {initialOrigin ? (
              <div>
                <div className="block text-xs font-semibold text-foreground mb-1.5">
                  Where it lives now
                </div>
                <div className="w-full px-3.5 py-2.5 rounded-xl border border-line bg-soft text-sm text-foreground dark:border-white/10 dark:bg-white/5 dark:text-white">
                  {initialOrigin}
                </div>
              </div>
            ) : (
              <div>
                <label htmlFor="mig-origin" className="block text-xs font-semibold text-foreground mb-1.5">
                  Where does it live now
                </label>
                <input
                  id="mig-origin"
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  placeholder={createProject ? "https://github.com/your-team/your-app" : "GitHub repo URL, or the platform you built it on"}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-line bg-white text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-plum/40 focus:ring-1 focus:ring-ring/40 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-plum/40 dark:focus:ring-ring/40"
                />
                {createProject && !projectId && (
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    The GitHub repository for your app. We bring it in as a new project for your architect; nothing is charged until you confirm.
                  </p>
                )}
                {originNotice}
              </div>
            )}
            <div>
              <label htmlFor="mig-quirks" className="block text-xs font-semibold text-foreground mb-1.5">
                Anything unusual we should know
              </label>
              <Textarea
                id="mig-quirks"
                value={quirks}
                onChange={(e) => setQuirks(e.target.value)}
                rows={2}
                placeholder="Custom backend, heavy integrations, a monorepo. This helps us start faster, it does not change the price."
                className="rounded-xl resize-none bg-white text-foreground placeholder:text-muted-foreground border-line focus:border-plum/40 focus-visible:ring-1 focus-visible:ring-ring/40 dark:bg-white/5 dark:text-white dark:placeholder:text-gray-500 dark:border-white/10 dark:focus:border-plum/40 dark:focus-visible:ring-ring/40"
              />
            </div>
          </div>
          {isGithubImport ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  type="button"
                  className="w-full text-left rounded-xl border border-line bg-selected p-4 hover:border-plum/40 hover:bg-selected transition-colors dark:border-plum/40 dark:bg-selected dark:hover:border-plum/40 dark:hover:bg-soft-lavender-900/30"
                >
                  <div className="text-sm font-semibold text-foreground">Try Import first, it is free</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    Import runs on your existing credits and tells you upfront what can come across and
                    what cannot. Most projects move on their own. If it stalls, you already know exactly
                    what an architect is picking up.
                  </div>
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white/95 backdrop-blur-md border-line shadow-soft sm:max-w-lg dark:bg-surface dark:border-white/10">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-foreground text-xl font-semibold">
                    Try the AI import instead?
                  </AlertDialogTitle>
                  <div className="text-muted-foreground mt-3 space-y-3">
                    <p className="text-sm leading-relaxed">
                      This resumes the AI-driven import for this project instead of engaging a Joylo
                      Architect. It runs on your existing credits, not the {formatUSD(SERVICE_COSTS.migration.displayUSD)} fee.
                      You can still engage an architect later if it stalls.
                    </p>
                  </div>
                </AlertDialogHeader>
                <AlertDialogFooter className="mt-4 sm:space-x-3">
                  <AlertDialogCancel className="border-line text-muted-foreground hover:bg-soft hover:text-foreground font-medium dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-gray-300">
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={switchToAiImport}
                    disabled={switchingToAi}
                    className="bg-gradient-sunset hover:opacity-90 text-white border-none "
                  >
                    {switchingToAi ? (
                      <span className="flex items-center gap-1.5">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Switching…
                      </span>
                    ) : (
                      "Try Import first"
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <div className="rounded-xl border border-line bg-selected p-4 dark:border-plum/40 dark:bg-selected">
              <div className="text-sm font-semibold text-foreground">Try Import first, it is free</div>
              <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Import runs on your existing credits and tells you upfront what can come across and
                what cannot. Most projects move on their own. If it stalls, you already know exactly
                what an architect is picking up.
              </div>
            </div>
          )}
        </div>
      )}

      {/* ---------- STEP: TIER (production) ---------- */}
      {step === "tier" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {PRODUCTION_TIERS.map((t) => {
              const cost = SERVICE_COSTS[t.key];
              const selected = tier === t.key;
              const planCovers = includedInPlan && t.key === "production_advanced";
              return (
                <button
                  key={t.key}
                  type="button"
                  disabled={includedInPlan}
                  onClick={() => setTier(t.key)}
                  className={`text-left rounded-xl border p-4 flex flex-col gap-2.5 transition-colors ${
                    includedInPlan
                      ? planCovers
                        ? "border-emerald-300 bg-emerald-50/60 cursor-default dark:border-emerald-800/50 dark:bg-emerald-950/20"
                        : "border-line bg-soft cursor-default dark:border-white/10 dark:bg-white/5"
                      : selected
                        ? "border-sunset-coral bg-sunset-coral/5 dark:border-rose-500/60 dark:bg-rose-950/20"
                        : "border-line hover:border-sunset-coral/40 bg-white dark:border-white/10 dark:hover:border-rose-500/30 dark:bg-white/5"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">{t.name}</span>
                    {planCovers && (
                      <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full dark:text-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-900/40">
                        Included
                      </span>
                    )}
                  </div>
                  <div>
                    <div className="text-xl font-bold text-foreground leading-none">
                      {planCovers ? "Included" : (
                        <>{formatUSD(cost.displayUSD)}<span className="text-xs font-normal text-muted-foreground">/mo</span></>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">{cost.hours} architect hours</div>
                  </div>
                  <ul className="space-y-1.5">
                    {t.items.map((i) => (
                      <li key={i.text} className={`text-xs leading-snug flex gap-1.5 ${i.dim ? "text-muted-foreground" : "text-muted-foreground"}`}>
                        <Check className={`w-3 h-3 mt-0.5 flex-shrink-0 ${i.dim ? "text-muted-foreground" : "text-success"}`} />
                        {i.text}
                      </li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900/40 dark:bg-emerald-950/30">
            <div className="text-sm font-semibold text-emerald-900 dark:text-emerald-300">Getting it back up is included</div>
            <div className="text-xs text-emerald-800 dark:text-emerald-300 mt-1 leading-relaxed">
              If something we monitor breaks, we restore it, up to 4 hours per incident. If an
              incident needs more than that, or what it really needs is an improvement rather than
              a repair, we stop and agree the extra work with you first.
            </div>
          </div>
          {!includedInPlan && (
            <p className="text-xs text-muted-foreground">
              Cover is bought one month at a time. Nothing renews automatically — you choose to
              extend at the end of each month.
            </p>
          )}
        </div>
      )}

      {/* ---------- STEP: CONFIRM ---------- */}
      {step === "confirm" && copy && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-line bg-gradient-to-b from-warm-cream-50 to-white p-4 text-sm dark:border-white/10 dark:from-white/5 dark:to-transparent">
            <div className="flex justify-between gap-3 pb-3 border-b border-line">
              <span className="text-muted-foreground">Service</span>
              <span className="font-semibold text-foreground text-right">
                {service === "development" ? `Development support — ${categoryLabel}`
                  : service === "migration" ? "Project migration"
                  : `Production support — ${SERVICE_COSTS[tier].label}`}
              </span>
            </div>

            <div className="py-3 space-y-2 border-b border-line">
              {/* requirePayment (fixed-price imports) never draws on the hours balance and
                  isn't sized in hours to the user — show the flat fee only, no hour counts. */}
              {!requirePayment && (
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Hours committed</span>
                  <span className="font-semibold text-foreground">{requiredHours}h</span>
                </div>
              )}
              {/* The balance is always drawn down first, and shown doing so */}
              {redeemFromBalance > 0 && (
                <div className="flex justify-between gap-3">
                  <span className="text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5">
                    <Wallet className="w-3.5 h-3.5" /> Redeemed from your balance
                  </span>
                  <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                    {redeemFromBalance}h · {formatUSD(0)}
                  </span>
                </div>
              )}
              {quote.needsPayment && (
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <CreditCard className="w-3.5 h-3.5" />
                    {requirePayment
                      ? "Migration fee"
                      : redeemFromBalance > 0 ? `Remaining ${quote.shortfallHours}h to buy` : `Buying ${quote.shortfallHours}h`}
                  </span>
                  <span className="font-semibold text-foreground">{formatUSD(quote.chargeUSD)}</span>
                </div>
              )}
              {quote.needsPayment && payment.addressValid && !resolvingTaxPreview && payment.preview && payment.preview.discount_amount > 0 && (
                <div className="flex justify-between gap-3">
                  <span className="text-emerald-700 dark:text-emerald-400">Voucher discount</span>
                  <span className="font-semibold text-emerald-700 dark:text-emerald-400">-{formatUSD(payment.preview.discount_amount)}</span>
                </div>
              )}
              {quote.needsPayment && payment.addressValid && (resolvingTaxPreview || (payment.preview && payment.preview.tax_amount > 0)) && (
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">{taxLabel}</span>
                  {resolvingTaxPreview ? (
                    <span className="h-3.5 w-12 bg-soft rounded animate-pulse dark:bg-white/10" />
                  ) : (
                    <span className="font-semibold text-foreground">{formatUSD(payment.preview!.tax_amount)}</span>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-between items-baseline gap-3 pt-3">
              <div>
                <div className="font-bold text-foreground">
                  {quote.needsPayment ? "Total due now" : "Nothing to pay"}
                </div>
                {!requirePayment && (
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Balance after this engagement: {remainingAfter}h
                  </div>
                )}
              </div>
              <div className={`text-2xl font-bold tracking-tight ${
                quote.needsPayment ? "text-foreground" : "text-emerald-700 dark:text-emerald-400"
              }`}>
                {resolvingTaxPreview ? (
                  <span className="inline-block h-6 w-20 bg-soft rounded animate-pulse align-middle dark:bg-white/10" />
                ) : (
                  formatUSD(displayTotalUSD)
                )}
              </div>
            </div>
          </div>

          {/* Address, then card fields — only when the balance genuinely cannot cover it */}
          {quote.needsPayment && !includedInPlan ? (
            <div className="rounded-2xl border border-line bg-soft p-4 space-y-3 dark:border-white/10 dark:bg-white/5">
              {paymentStep === "address" ? (
                <>
                  <p className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground flex items-center gap-1.5">
                    <CreditCard className="w-3.5 h-3.5" /> Billing address
                  </p>
                  <HoursAddressFields
                    addressLine1={payment.addressLine1}
                    setAddressLine1={payment.setAddressLine1}
                    city={payment.city}
                    setCity={payment.setCity}
                    state={payment.state}
                    setState={payment.setState}
                    billingCountry={payment.billingCountry}
                    setBillingCountry={payment.setBillingCountry}
                    postalCode={payment.postalCode}
                    setPostalCode={payment.setPostalCode}
                    countries={payment.countries}
                    processing={payment.processing}
                    compact
                  />
                  <TaxAndBusinessFields
                    customerType={payment.customerType}
                    onCustomerTypeChange={payment.setCustomerType}
                    businessName={payment.businessName}
                    onBusinessNameChange={payment.setBusinessName}
                    taxIdType={payment.taxIdType}
                    onTaxIdTypeChange={payment.setTaxIdType}
                    taxIdValue={payment.taxIdValue}
                    onTaxIdValueChange={payment.setTaxIdValue}
                    disabled={payment.processing}
                  />
                  {/* Applied here (not on the payment step below) so its discount is already
                      reflected in the tax preview above by the time the card is shown. */}
                  <PromoCodeInput
                    checkPending={false}
                    originalPrice={quote.chargeUSD}
                    authToken={token ?? ""}
                    onApplied={(details) => payment.setAppliedVoucherCode(details.code)}
                    onCleared={() => payment.setAppliedVoucherCode(null)}
                  />
                  <Button
                    onClick={payment.submitAddress}
                    disabled={!payment.addressValid}
                    className="w-full bg-gradient-sunset text-white"
                  >
                    Continue to payment
                  </Button>
                </>
              ) : (
                <>
                  <button
                    onClick={payment.goBackToAddress}
                    disabled={payment.processing}
                    className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" /> Back to address
                  </button>
                  <p className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground flex items-center gap-1.5">
                    <CreditCard className="w-3.5 h-3.5" /> Secure payment
                    {!requirePayment && (
                      <span className="normal-case tracking-normal font-normal text-muted-foreground">
                        · for the {quote.shortfallHours}h you are short
                      </span>
                    )}
                  </p>
                  {!payment.resolvingPreview && payment.preview && (
                    <WalletPayButton
                      amountCents={payment.preview.total * 100}
                      billingDetails={payment.walletBillingDetails}
                      disabled={busy || includedInPlan}
                      canOpen={() => payment.addressValid}
                      onPaymentMethod={submit}
                      onError={(message) => toast({ variant: "destructive", title: "Payment failed", description: message })}
                    />
                  )}
                  <HoursPaymentFields
                    cardholderName={payment.cardholderName}
                    setCardholderName={payment.setCardholderName}
                    cardError={payment.cardError}
                    handleCardChange={payment.handleCardChange}
                    processing={payment.processing}
                    compact
                  />
                </>
              )}
            </div>
          ) : !includedInPlan && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3 dark:border-emerald-900/40 dark:bg-emerald-950/30">
              <Sparkles className="w-4 h-4 text-emerald-700 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-sm font-semibold text-emerald-900 dark:text-emerald-300">
                  Covered by your balance — no card needed
                </div>
                <div className="text-xs text-emerald-800 dark:text-emerald-300 mt-0.5 leading-relaxed">
                  {requiredHours}h comes straight out of your {availableWhole}h, and only time the
                  architect actually logs is spent.
                </div>
              </div>
            </div>
          )}

          {copy.scope && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-4 border-t border-line">
              <div>
                <h6 className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 mb-2">Included</h6>
                <ul className="space-y-1.5">
                  {copy.scope.included.map((x) => (
                    <li key={x} className="text-xs text-muted-foreground leading-snug flex gap-1.5">
                      <Check className="w-3 h-3 mt-0.5 flex-shrink-0 text-success" />{x}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h6 className="text-xs font-semibold text-muted-foreground mb-2">Not included</h6>
                <ul className="space-y-1.5">
                  {copy.scope.excluded.map((x) => (
                    <li key={x} className="text-xs text-muted-foreground leading-snug flex gap-1.5">
                      <span className="w-3 h-[1.5px] bg-soft-gray-300 dark:bg-gray-600 mt-2 flex-shrink-0" />{x}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="pt-4 border-t border-line">
            <p className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground mb-3">
              What happens next
            </p>
            <ol className="space-y-2.5">
              {copy.steps.map((s, i) => (
                <li key={s} className="text-xs text-muted-foreground leading-relaxed flex gap-2.5">
                  <span className="w-5 h-5 flex-shrink-0 rounded-full bg-soft border border-line text-plum text-[10px] font-semibold flex items-center justify-center dark:bg-white/5 dark:border-white/10 dark:text-plum">
                    {i + 1}
                  </span>
                  {s}
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </>
  );

  const footerButtons = (
    <>
      {step !== "service" && (
        <Button
          variant="outline"
          onClick={back}
          disabled={busy}
          // hover:text is set explicitly: the outline variant's hover:text-accent-foreground
          // would otherwise win and leave the label unreadable against the cream hover fill.
          className="bg-white text-foreground border-line hover:bg-soft hover:text-foreground hover:border-plum/40 sm:mr-auto transition-colors dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10 dark:hover:text-white dark:hover:border-plum/40"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      )}

      {step === "category" && (
        <Button onClick={() => setStep("hours")} disabled={!category} className="bg-gradient-sunset text-white">
          Next
        </Button>
      )}
      {step === "hours" && (
        <Button onClick={() => setStep("confirm")} disabled={requiredHours <= 0} className="bg-gradient-sunset text-white">
          Next
        </Button>
      )}
      {step === "details" && (
        <Button
          onClick={() => setStep("confirm")}
          disabled={!!createProject && !projectId && (!originRepo || !originReady)}
          className="bg-gradient-sunset text-white"
        >
          Next
        </Button>
      )}
      {step === "tier" && (
        <Button
          onClick={() => setStep("confirm")}
          disabled={includedInPlan}
          className="bg-gradient-sunset text-white"
        >
          {includedInPlan ? "Already active" : "Next"}
        </Button>
      )}
      {step === "confirm" && (
        <Button
          onClick={() => submit()}
          disabled={
            busy || includedInPlan ||
            (quote.needsPayment && (paymentStep === "address" || !payment.ready || payment.resolvingPreview))
          }
          className="bg-gradient-sunset text-white"
        >
          {busy
            ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />{payment.processingStep || "Submitting…"}</>
            : <><Check className="w-4 h-4 mr-2" />{ctaLabel()}</>}
        </Button>
      )}
    </>
  );

  const hintText = step === "confirm" && (
    <p className="text-[11px] text-muted-foreground text-center leading-relaxed -mt-1">{hint()}</p>
  );

  // An engagement already open for this project (pending/assigned/in_progress) makes a new
  // purchase meaningless — the toolbar's own "Engage Expert" button already disables itself
  // the same way (see ToolBar.tsx), but this chooser is also reachable directly (project
  // settings, Home/Help/Billing menus) without going through that button at all. Gated on
  // hasActiveEngagement (project-scoped) rather than a locally-owned activeRequest, so a
  // collaborator who isn't the original requester sees the same block instead of being able
  // to buy a second, conflicting engagement — the server enforces this too (POST /engagement/
  // request), this is just surfacing it before the user gets that far.
  const activeStatusLabel = activeEngagementStatus === "pending"
    ? "Request pending"
    : activeEngagementStatus === "assigned"
      ? "Under review"
      : "Developer assigned";
  const alreadyEngagedBody = (
    <div className="flex flex-col items-center gap-3 py-6 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/40">
        <Check className="h-6 w-6 text-success" />
      </div>
      <div>
        <p className="font-semibold text-foreground">This project already has an active engagement</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {activeStatusLabel} for this project — a new request can't be started until it's completed.
        </p>
      </div>
    </div>
  );

  // Migration and production have no category of their own, so the intent is carried in the
  // description where whoever picks the request up will read it.
  const configurator = hasActiveEngagement ? (
    embedded ? (
      <div className="flex flex-col gap-4 p-4 sm:p-6 bg-white text-foreground dark:bg-surface dark:text-white">
        {alreadyEngagedBody}
      </div>
    ) : (
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="sm:max-w-md bg-white text-foreground dark:bg-surface dark:text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-foreground">Engage a Joylo expert</DialogTitle>
          </DialogHeader>
          {alreadyEngagedBody}
          <DialogFooter>
            <Button onClick={() => close(false)} className="w-full sm:w-auto">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  ) : embedded ? (
    !isFreshMigrationPick && (
      // [&>*]:shrink-0 — flex items default to flex-shrink:1, so once the grid->flex-col swap
      // above landed, sections like the wallet strip could get compressed below their natural
      // height (clipping the icon badge) instead of the container just scrolling past them.
      <div ref={topRef} className="flex flex-col gap-4 p-4 sm:p-6 bg-white text-foreground dark:bg-surface dark:text-white [&>*]:shrink-0">
        <div className="flex flex-col space-y-1.5 text-center sm:text-left">
          <h2 className="text-xl font-bold text-foreground">{titleText}</h2>
          <p className="text-sm text-muted-foreground">{descriptionContent}</p>
        </div>
        {stepsContent}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 flex-col sm:flex-row gap-2 sm:items-center">
          {footerButtons}
        </div>
        {hintText}
      </div>
    )
  ) : (
    <Dialog open={open && !isFreshMigrationPick} onOpenChange={close}>
      {/* DialogContent's shared grid template (grid-rows-[minmax(0,1fr)]) only defines ONE
          explicit row — handing it DialogHeader/stepsContent's several siblings/DialogFooter/
          hintText as flat direct children left the grid to auto-place ~6+ items against that
          single track, which could squeeze the header below its natural height instead of
          sizing it normally. Wrapping everything in one child means DialogContent always has
          exactly one grid item (filling that track correctly, scrolling internally via min-h-0),
          and plain flex-col stacking inside it can't hit the same ambiguity. */}
      <DialogContent className="sm:max-w-2xl max-h-[88vh] bg-white text-foreground dark:bg-surface dark:text-white">
        <div ref={topRef} className="flex flex-col gap-4 min-h-0 overflow-y-auto [&>*]:shrink-0">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-foreground">
              {titleText}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {descriptionContent}
            </DialogDescription>
          </DialogHeader>

          {stepsContent}

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:items-center">
            {footerButtons}
          </DialogFooter>

          {hintText}
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
    {configurator}
    <GithubImportModal
      isOpen={showGithubImport}
      viaMigrationService
      onClose={() => {
        setShowGithubImport(false);
        // Cancelling the repo picker backs all the way out — there is no meaningful
        // "return to the service list" once migration has handed off to this flow.
        close(false);
      }}
      onImported={() => {
        setShowGithubImport(false);
        onEngaged?.();
      }}
    />
    </>
  );
}
